import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, AlertCircle, User, Calendar, CreditCard, Building2, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return 'N/A';
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  } catch {
    return 'N/A';
  }
};

const VerifyMemberPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t');
  
  const [loading, setLoading] = useState(true);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (patientId) {
      verifyMembership();
    }
  }, [patientId, token]);

  const verifyMembership = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/members/verify/${patientId}`, {
        params: { token }
      });
      setVerificationResult(response.data);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.detail || 'Failed to verify membership');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-16 w-16 text-teal-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-semibold text-gray-800">Verifying Membership...</h2>
            <p className="text-gray-600 mt-2">Please wait while we check the membership status.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <div className="bg-red-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <XCircle className="h-12 w-12 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-800">Verification Failed</h2>
            <p className="text-gray-600 mt-2">{error}</p>
            <p className="text-sm text-gray-500 mt-4">
              If you believe this is an error, please contact the clinic.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { member, clinic, plan, status } = verificationResult || {};
  const isActive = status === 'active';
  const isExpired = status === 'expired';

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className={`w-full max-w-md ${isActive ? 'border-green-200' : isExpired ? 'border-red-200' : 'border-yellow-200'}`}>
        <CardContent className="p-0">
          {/* Header with status */}
          <div 
            className={`p-6 text-white text-center ${
              isActive ? 'bg-gradient-to-r from-green-500 to-green-600' : 
              isExpired ? 'bg-gradient-to-r from-red-500 to-red-600' : 
              'bg-gradient-to-r from-yellow-500 to-yellow-600'
            }`}
          >
            <div className="mb-3">
              {isActive ? (
                <CheckCircle2 className="h-16 w-16 mx-auto" />
              ) : isExpired ? (
                <XCircle className="h-16 w-16 mx-auto" />
              ) : (
                <AlertCircle className="h-16 w-16 mx-auto" />
              )}
            </div>
            <h1 className="text-2xl font-bold">
              {isActive ? 'Active Membership' : isExpired ? 'Expired Membership' : 'Inactive Membership'}
            </h1>
            <p className="text-white/80 mt-1">
              {isActive ? 'This membership is valid and active' : 
               isExpired ? 'This membership has expired' : 
               'This membership is not currently active'}
            </p>
          </div>

          {/* Member Details */}
          <div className="p-6 space-y-4">
            {/* Clinic Info */}
            {clinic && (
              <div className="flex items-center gap-3 pb-4 border-b">
                {clinic.logo_url ? (
                  <img 
                    src={clinic.logo_url} 
                    alt={clinic.name} 
                    className="h-12 w-12 object-contain rounded-lg border"
                  />
                ) : (
                  <div className="h-12 w-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-teal-600" />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-gray-900">{clinic.name}</p>
                  <p className="text-sm text-gray-500">Medical Center</p>
                </div>
              </div>
            )}

            {/* Member Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="bg-gray-100 p-2 rounded-lg">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Member Name</p>
                  <p className="font-semibold text-gray-900">{member?.full_name || 'N/A'}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="bg-gray-100 p-2 rounded-lg">
                  <CreditCard className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Member ID</p>
                  <p className="font-mono font-semibold text-gray-900">{member?.patient_id || 'N/A'}</p>
                </div>
              </div>

              {plan && (
                <div className="flex items-center gap-3">
                  <div className="bg-gray-100 p-2 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Membership Plan</p>
                    <p className="font-semibold text-gray-900">{plan.name}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="bg-gray-100 p-2 rounded-lg">
                  <Calendar className="h-5 w-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Valid Through</p>
                  <p className={`font-semibold ${isActive ? 'text-green-600' : 'text-red-600'}`}>
                    {formatDate(member?.membership_end_date)}
                  </p>
                </div>
              </div>
            </div>

            {/* Verification timestamp */}
            <div className="pt-4 border-t text-center">
              <p className="text-xs text-gray-400">
                Verified on {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyMemberPage;
